// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react'
import { Redirect } from 'react-router-dom'

import { observer } from 'mobx-react'
import { Button, Container, Dropdown, Form, Header, Message, Segment } from 'semantic-ui-react'

import { isAuthenticated } from 'services/self'
import { isPolicyStateChangeEnabled } from 'services/config'
import {
  PRODUCT_OPTIONS,
  STATUS_OPTIONS,
  requestPolicyStateChange
} from 'services/policy-state-change'
import { store } from 'services/state'

const WAIT_DISCLAIMER = 'Only request a status change at least 5 minutes after the proposal was created, or after the last status change.'

function messageFromBody(body) {
  if (!body) return ''
  if (typeof body === 'string') {
    try {
      return messageFromBody(JSON.parse(body))
    } catch (ignored) {
      return body
    }
  }
  return typeof body.message === 'string' ? body.message : ''
}

// The generated API Gateway client rejects with { data }, not axios { response }.
function errorMessageFrom(error) {
  return messageFromBody(error && error.data) ||
    messageFromBody(error && error.response && error.response.data) ||
    'Proposal status could not be changed. Please try again later.'
}

export default observer(class PolicyStateChange extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      product: 'proposal',
      proposalId: '',
      action: 'approve',
      isSubmitting: false,
      error: '',
      result: undefined
    }
  }

  handleChange = (event, data) => {
    const name = data && data.name ? data.name : event.target.name
    const value = data && Object.prototype.hasOwnProperty.call(data, 'value') ? data.value : event.target.value
    this.setState({ [name]: value, error: '', result: undefined })
  }

  handleSubmit = (event) => {
    event.preventDefault()
    if (this.state.isSubmitting || !/^[0-9]+$/.test(this.state.proposalId.trim())) {
      this.setState({ error: 'Enter the numeric proposal id returned by proposal submission.' })
      return
    }
    if (!store.idToken || !store.apiKey) {
      this.setState({ error: 'Complete API Authentication before changing a proposal status.' })
      return
    }

    const submitted = {
      product: this.state.product,
      proposalId: this.state.proposalId.trim(),
      action: this.state.action
    }
    this.setState({ isSubmitting: true, error: '', result: undefined })
    requestPolicyStateChange({
      ...submitted,
      statusToken: store.idToken,
      apiKey: store.apiKey,
      reporter: store.username
    }).then((result) => {
      this.setState({ isSubmitting: false, result: { ...result, submitted } })
    }).catch((error) => {
      this.setState({ isSubmitting: false, error: errorMessageFrom(error) })
    })
  }

  resultText() {
    const { result } = this.state
    if (!result) return ''
    const { proposalId, action } = result.submitted
    const requested = action === 'decline' ? 'decline' : action === 'in-force' ? 'inforce' : 'approve'
    return `Request to ${requested} proposal ${proposalId.trim()} was sent successfully.`
  }

  render() {
    if (!isAuthenticated() || !isPolicyStateChangeEnabled()) return <Redirect to="/" />

    return (
      <Container style={{ padding: '40px' }}>
        <Header as="h1">Policy State Change</Header>
        <Message warning content={WAIT_DISCLAIMER} />
        <Segment>
          <Form onSubmit={this.handleSubmit} error={Boolean(this.state.error)} success={Boolean(this.state.result)}>
            <Form.Field>
              <label htmlFor="policy-state-product">Product</label>
              <Dropdown
                id="policy-state-product"
                name="product"
                selection
                options={PRODUCT_OPTIONS}
                value={this.state.product}
                onChange={this.handleChange}
              />
            </Form.Field>
            <Form.Input
              label="Proposal ID"
              name="proposalId"
              value={this.state.proposalId}
              onChange={this.handleChange}
              placeholder="Enter the proposal id"
            />
            <Form.Field>
              <label htmlFor="policy-state-action">Status</label>
              <Dropdown
                id="policy-state-action"
                name="action"
                selection
                options={STATUS_OPTIONS}
                value={this.state.action}
                onChange={this.handleChange}
              />
            </Form.Field>
            <Form.Field>
              <label>Reporter</label>
              <p>{store.username || 'Signed-in portal user'}</p>
            </Form.Field>
            <Message error content={this.state.error} />
            <Message success content={this.resultText()} />
            <Button primary type="submit" loading={this.state.isSubmitting} disabled={this.state.isSubmitting}>
              Change status
            </Button>
          </Form>
        </Segment>
      </Container>
    )
  }
})
